import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useApi } from "../../hooks/useApi";
import { GoogleSheetMid } from "../../types/googleSheetMid";

const brandButtonSx = {
  backgroundColor: "#465fff",
  "&:hover": { backgroundColor: "#3641f5" },
} as const;

interface Props {
  companyId: string;
}

export default function GoogleSheetMidsManager({ companyId }: Props) {
  const { get, post, put, del, loading } = useApi();

  const [mids, setMids] = useState<GoogleSheetMid[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState("");

  const [editing, setEditing] = useState<GoogleSheetMid | null>(null);
  const [editForm, setEditForm] = useState({ mid: "", pattern: "" });

  const [deleting, setDeleting] = useState<GoogleSheetMid | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const baseUrl = `/api/companies/${companyId}/google-sheet-mids`;

  const fetchAll = useCallback(async () => {
    try {
      setMids(await get<GoogleSheetMid[]>(baseUrl));
      setFetchError(null);
    } catch (err: any) {
      setFetchError(err.message || "Failed to load Google Sheet MIDs");
    }
  }, [get, baseUrl]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const closeAdd = () => {
    setAddOpen(false);
    setAddText("");
    setApiError(null);
  };

  const openEdit = (row: GoogleSheetMid) => {
    setEditing(row);
    setEditForm({ mid: row.mid, pattern: row.pattern ?? "" });
    setApiError(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setApiError(null);
  };

  const submitAdd = async () => {
    const names = addText.split("\n").map(line => line.trim()).filter(Boolean);
    if (names.length === 0) {
      setApiError("Enter at least one MID");
      return;
    }
    try {
      await post(baseUrl, { mids: names });
      setSnackbar({ open: true, message: `${names.length} MID${names.length !== 1 ? "s" : ""} added` });
      closeAdd();
      fetchAll();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  const submitEdit = async () => {
    if (!editing) return;
    const mid = editForm.mid.trim();
    if (!mid) {
      setApiError("MID is required");
      return;
    }
    try {
      await put(`${baseUrl}/${editing.id}`, { mid, pattern: editForm.pattern.trim() });
      setSnackbar({ open: true, message: "MID updated" });
      closeEdit();
      fetchAll();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await del(`${baseUrl}/${deleting.id}`);
      setSnackbar({ open: true, message: "MID deleted" });
      setDeleting(null);
      fetchAll();
    } catch (err: any) {
      setApiError(err.message || "An error occurred");
    }
  };

  return (
    <div>
      <Box mb={2} display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
        <Typography variant="body2" color="text.secondary">
          The MID each CheckoutChamp gateway belongs to in the Google Sheets. A gateway matches when
          its title contains the pattern, ignoring case; one MID can have several patterns.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{ ...brandButtonSx, flexShrink: 0, textTransform: "none" }}
        >
          Add MIDs
        </Button>
      </Box>

      {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "40%" }}>Google Sheet MID</TableCell>
                <TableCell>Gateway title pattern</TableCell>
                <TableCell sx={{ width: 100 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mids.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.mid}</TableCell>
                  <TableCell>
                    {row.pattern ?? (
                      <Typography variant="body2" color="text.secondary">No pattern yet</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => { setApiError(null); setDeleting(row); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {mids.length === 0 && (
          <Box className="text-center py-8">
            <Typography variant="body1" color="text.secondary">
              No MIDs yet. Paste your list with “Add MIDs”, then give each one its gateway pattern.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add MIDs</DialogTitle>
        <DialogContent>
          {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
          <TextField
            label="MIDs"
            value={addText}
            onChange={e => setAddText(e.target.value)}
            fullWidth
            multiline
            minRows={6}
            autoFocus
            disabled={loading}
            sx={{ mt: 1 }}
            helperText="One per line. Patterns are added afterwards, by editing each row."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAdd} disabled={loading}>Cancel</Button>
          <Button onClick={submitAdd} variant="contained" disabled={loading} sx={brandButtonSx}>
            {loading ? "Saving..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editing !== null} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit MID</DialogTitle>
        <DialogContent>
          {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Google Sheet MID"
              value={editForm.mid}
              onChange={e => setEditForm(prev => ({ ...prev, mid: e.target.value }))}
              fullWidth
              required
              size="small"
              autoFocus
              disabled={loading}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Gateway title pattern"
              value={editForm.pattern}
              onChange={e => setEditForm(prev => ({ ...prev, pattern: e.target.value }))}
              fullWidth
              size="small"
              disabled={loading}
              inputProps={{ maxLength: 255 }}
              helperText="Matches any CheckoutChamp gateway whose title contains this text, ignoring case. Leave empty until you know it."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} disabled={loading}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained" disabled={loading} sx={brandButtonSx}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete MID</DialogTitle>
        <DialogContent>
          {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
          <Typography>
            Delete <strong>{deleting?.mid}</strong>
            {deleting?.pattern ? <> and its pattern <strong>{deleting.pattern}</strong></> : null}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleting(null)} disabled={loading}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
